import { useEffect, useRef } from 'react';
import {
  InstrumentStoreItem,
  InstrumentSubscriptionDataScope,
  ListedInstrument,
  SubscribeList,
  SubscribeOptions,
} from '../../types';
import { ObjectSet } from '../../utils';
import { getSubscribeOptions } from './eStreamUtils';

type CallbackType<T> = T extends Array<ListedInstrument> ? ObjectSet<InstrumentStoreItem> : InstrumentStoreItem;

export type SubscribeToInstrumentChangesFn = (
  pOptions: Array<SubscribeOptions> | SubscribeList,
  onInstrumentChange?: (items: ObjectSet<InstrumentStoreItem>) => void,
) => () => void;

/**
 * Hook for subscribing to instrument changes callback from eStream.
 *
 * ⚠️If you need data from the instrument store, use useInstrumentStoreItem instead.
 *
 * Do not extend react-hooks/exhaustive-deps eslint rule with this hook, because onChange is a callback function and
 * React cannot resolve its dependencies.
 *
 * @param source identifier of the subscription
 * @param instrument instrument to subscribe to
 * @param disableSubscription when set to true it will not subscribe to instrument changes
 * @param dataScope data scope to subscribe to
 * @param onChange callback function called when instrument changes
 * @param forceRequestStaticData when set to true it will require static data to be sent with the first update using REST
 * @param additionalDependencies additional dependencies for the hook
 * @param subscribeToInstrumentChanges
 */
export const useSubscribeToInstrumentChanges = <
  ListedInstrumentType extends ListedInstrument | Array<ListedInstrument> | null | undefined,
  CallbackParamType extends CallbackType<ListedInstrumentType>,
>(
  {
    source,
    instrument,
    disableSubscription,
    dataScope,
    onChange,
    forceRequestStaticData,
    subscribeToInstrumentChanges,
  }: {
    source: string;
    instrument: ListedInstrumentType;
    onChange?: (instrumentStoreItem: CallbackParamType) => void;
    disableSubscription?: boolean;
    dataScope?: Array<InstrumentSubscriptionDataScope>;
    forceRequestStaticData?: boolean;
    subscribeToInstrumentChanges: SubscribeToInstrumentChangesFn;
  },
  additionalDependencies: Array<unknown> = [],
) => {
  const previousInstrumentChangesListener = useRef<() => void>(undefined);

  useEffect(() => {
    if (disableSubscription) {
      return () => {};
    }

    if (previousInstrumentChangesListener.current) {
      previousInstrumentChangesListener.current();
      previousInstrumentChangesListener.current = undefined;
    }
    if (instrument && ((Array.isArray(instrument) && instrument.length > 0) || !Array.isArray(instrument))) {
      previousInstrumentChangesListener.current = subscribeToInstrumentChanges(
        getSubscribeOptions(source, instrument, dataScope, forceRequestStaticData),
        onChange
          ? items => {
              if (Array.isArray(instrument)) {
                (onChange as (p: CallbackType<Array<ListedInstrument>>) => void)(items);
                return;
              }
              const changedItem = items.get(instrument);
              if (changedItem) {
                (onChange as (p: CallbackType<ListedInstrument>) => void)(changedItem);
              }
            }
          : undefined,
      );
    }
    return () => {
      if (previousInstrumentChangesListener.current) {
        previousInstrumentChangesListener.current();
        previousInstrumentChangesListener.current = undefined;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- onChange is a callback function, it should not be in the dependency array
  }, [instrument, source, subscribeToInstrumentChanges, disableSubscription, ...additionalDependencies]);
};
